import React, { useMemo, useState, useCallback } from 'react';
import { useAppointments, useOpsSummary, downloadReport } from '@/services/api';
import { Role, useAuth } from '@/state/AuthContext';
import useMockDataToast from '@/hooks/useMockDataToast';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';const rolePlaybooks: Record<
  Role,
  { title: string; headline: string; actions: { label: string; detail: string }[] }
> = {
  medical_director: {
    title: '領導工作重點',
    headline: '維持 ICU 容量順暢並核准升級案件。',
    actions: [
      { label: '檢閱 ICU 佇列', detail: '有 2 件案例等待醫療總監簽核。' },
      { label: '核准用藥單', detail: '有 3 筆管制藥物訂單待合規審查。' },
      { label: '人力現況', detail: '確認夜班手術人力配置。' },
    ],
  },
  surgery: {
    title: '外科重點',
    headline: '為骨科與軟組織手術做好團隊準備。',
    actions: [
      { label: '術前討論', detail: '確保 4 位病患已完成禁食確認。' },
      { label: '器械檢點', detail: '14:00 手術前確認 OR2 器械盤已滅菌。' },
      { label: '恢復室轉床', detail: '與 ICU 協調術後交接時段。' },
    ],
  },
  internal_med: {
    title: '內科作業',
    headline: '穩定複雜病例並對齊會診計畫。',
    actions: [
      { label: '重新檢視鑑別診斷列表', detail: '有 2 份轉診待主治更新。' },
      { label: '飼主諮詢準備', detail: '中午回電前先準備影像資料。' },
      { label: '照護協調', detail: '與 ICU 與藥局同步化驗追蹤。' },
    ],
  },
  customer_service: {
    title: '客服指標',
    headline: '維持報到時間低於 4 分鐘並升級帳務風險。',
    actions: [
      { label: '報到佇列', detail: '30 分鐘內將有 4 位抵達。' },
      { label: '帳務聯繫', detail: '今天致電餘額超過 500 美元的客戶。' },
      { label: '出院資料', detail: '列印下午 3 件手術的指示。' },
    ],
  },
  billing: {
    title: '營收作業',
    headline: '整理應收款、核准付款計畫並降低稽核風險。',
    actions: [
      { label: '高額帳款檢視', detail: '有 3 筆帳單需要主管核准。' },
      { label: '保險佇列', detail: '追蹤 2 件延宕超過 10 天的理賠。' },
      { label: '稽核焦點', detail: '確認麻醉藥紀錄與帳單一致。' },
    ],
  },
};

const criticalAlerts = [
  { 
    icon: '💊', 
    title: '藥局庫存警示', 
    detail: '酮胺存量低於 15 劑，已觸發自動補貨',
    priority: 'high'
  },
  { 
    icon: '🔬', 
    title: '設備維護提醒', 
    detail: '血液分析儀需要校正，預計下午 14:00 完成',
    priority: 'medium'
  },
  { 
    icon: '🏥', 
    title: '復原區容量', 
    detail: '2 個 ICU 籠位預留至 19:00，請協調術後安排',
    priority: 'medium'
  },
  { 
    icon: '⚡', 
    title: '緊急物資', 
    detail: '無菌手術包存量充足，本週已補貨完成',
    priority: 'low'
  },
];

// Mock pending tasks data
const pendingTasks = [
  { 
    id: 1, 
    title: 'Luna 的血檢報告審閱', 
    dueDate: '今天 14:00',
    priority: 'high',
    type: 'review'
  },
  { 
    id: 2, 
    title: '核准新進藥品訂單', 
    dueDate: '今天 16:00',
    priority: 'high',
    type: 'approval'
  },
  { 
    id: 3, 
    title: '回覆飼主諮詢訊息 (3 則)', 
    dueDate: '今天結束前',
    priority: 'medium',
    type: 'communication'
  },
  { 
    id: 4, 
    title: '完成 ICU 護理記錄', 
    dueDate: '明天 09:00',
    priority: 'medium',
    type: 'documentation'
  },
];

// Mock revenue trend data (last 7 days)
const revenueTrend = [1200, 1800, 1500, 2200, 1900, 2400, 2020];
const revenueDayLabels = ['一', '二', '三', '四', '五', '六', '日'];
// Drill-down mock breakdown per day
const revenueBreakdown: Array<{ day: string; total: number; items: { label: string; amount: number }[] }> = revenueTrend.map((val, i) => ({
  day: revenueDayLabels[i],
  total: val,
  items: [
    { label: '門診', amount: Math.round(val * 0.35) },
    { label: '手術', amount: Math.round(val * 0.4) },
    { label: '藥局', amount: Math.round(val * 0.15) },
    { label: '化驗', amount: Math.round(val * 0.1) },
  ],
}));

const formatMetricValue = (id: string, value: number) => {
  if (id === 'revenue') {
    return `$${Math.round(value).toLocaleString()}`;
  }
  return value.toLocaleString();
};

const Dashboard: React.FC = () => {
  const { data: opsSummary, isFallback, fallbackMessage } = useOpsSummary();
  useMockDataToast(isFallback, '營運摘要暫時離線，顯示快取資料。', fallbackMessage);
  const { data: appointments = [] } = useAppointments();
  const { role } = useAuth();
  const navigate = useNavigate();
  const playbook = role ? rolePlaybooks[role] : rolePlaybooks.medical_director;
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);

  const appointmentPreview = useMemo(() => appointments.slice(0, 5), [appointments]);

  const statusTotal =
    opsSummary.appointmentStatus.reduce((total, status) => total + status.count, 0) || 1;
  const revenueTotal = opsSummary.revenue.pending + opsSummary.revenue.collected;
  const revenueProgress = revenueTotal
    ? Math.round((opsSummary.revenue.collected / revenueTotal) * 100)
    : 0;

  const nextAlert = () => {
    setCurrentAlertIndex((prev) => (prev + 1) % criticalAlerts.length);
  };

  const prevAlert = () => {
    setCurrentAlertIndex((prev) => (prev - 1 + criticalAlerts.length) % criticalAlerts.length);
  };

  const maxRevenue = Math.max(...revenueTrend);
  const currentAlert = criticalAlerts[currentAlertIndex];

  // Chart interactivity state
  const [activeRevenueIndex, setActiveRevenueIndex] = useState<number | null>(null);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [filteredRevenueIndex, setFilteredRevenueIndex] = useState<number | null>(null);

  const handleBarFocus = (idx: number) => setActiveRevenueIndex(idx);
  const clearBarFocus = () => setActiveRevenueIndex(null);
  const toggleFilterDay = (idx: number) => {
    setFilteredRevenueIndex(prev => prev === idx ? null : idx);
  };
  const openDrillDown = (idx: number) => {
    setActiveRevenueIndex(idx);
    setShowRevenueModal(true);
  };
  const closeDrillDown = () => setShowRevenueModal(false);

  // Keyboard navigation for sparkline (ArrowLeft/ArrowRight, Enter for drill-down, Space toggle filter)
  const handleSparklineKey = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      setActiveRevenueIndex(prev => {
        const next = prev == null ? 0 : (prev + 1) % revenueTrend.length;
        return next;
      });
      e.preventDefault();
    } else if (e.key === 'ArrowLeft') {
      setActiveRevenueIndex(prev => {
        const next = prev == null ? revenueTrend.length - 1 : (prev - 1 + revenueTrend.length) % revenueTrend.length;
        return next;
      });
      e.preventDefault();
    } else if (e.key === 'Enter' && activeRevenueIndex != null) {
      openDrillDown(activeRevenueIndex);
      e.preventDefault();
    } else if ((e.key === ' ' || e.key === 'Spacebar') && activeRevenueIndex != null) {
      toggleFilterDay(activeRevenueIndex);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      clearBarFocus();
      e.preventDefault();
    }
  }, [activeRevenueIndex]);

  const effectiveTrend = filteredRevenueIndex != null
    ? revenueTrend.map((v, i) => (i === filteredRevenueIndex ? v : 0))
    : revenueTrend;

  // Metric card navigation mapping
  const metricNavigation: Record<string, string> = {
    'today-appts': '/appointments',
    'active-patients': '/patients',
    'revenue': '/billing',
    'pending-labs': '/patients',
  };

  return (
    <div className="space-y-8">
      {/* Operational Overview - Enhanced with better visual hierarchy */}
      <section>
        <h2 className="section-title mb-4">營運總覽</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {opsSummary.metrics.map((metric, idx) => (
            <button
              key={metric.id}
              onClick={() => metricNavigation[metric.id] && navigate(metricNavigation[metric.id])}
              className={`glass-panel p-5 text-left transition-all hover:shadow-lg hover:scale-105 touch-manipulation min-h-[120px] ${
                idx === 0 ? 'ring-2 ring-brand-blue/20' : ''
              } ${metricNavigation[metric.id] ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {metric.label}
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <p className="text-3xl font-semibold text-slate-900">
                  {formatMetricValue(metric.id, metric.value)}
                </p>
                {metric.trend.includes('↑') && (
                  <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-500" />
                )}
                {metric.trend.includes('↓') && (
                  <ArrowTrendingDownIcon className="h-5 w-5 text-rose-500" />
                )}
              </div>
              <p className={`mt-1 text-xs font-medium ${
                metric.trend.includes('↑') ? 'text-emerald-600' : 'text-slate-500'
              }`}>
                {metric.trend}
              </p>
            </button>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        {/* Today's Appointments Table */}
        <div className="glass-panel lg:col-span-2">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <p className="section-title mb-1">今日預約</p>
              <h3 className="text-lg font-semibold text-slate-900">分級與處置</h3>
            </div>
            <button
              type="button"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-brand-blue hover:text-brand-blue"
              onClick={async () => {
                try {
                  const blob = await downloadReport({ topic: 'performance', format: 'csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'schedule.csv';
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  console.warn('匯出失敗', err);
                }
              }}
            >
              匯出行程
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] table-fixed divide-y divide-slate-100">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-6 py-3">病患</th>
                  <th className="px-6 py-3">飼主</th>
                  <th className="px-6 py-3">時間</th>
                  <th className="px-6 py-3">主治醫師</th>
                  <th className="px-6 py-3">狀態</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white text-sm text-slate-700">
                {appointmentPreview.map((appt) => {
                  const time = new Date(appt.date).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  return (
                    <tr key={appt.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{appt.petName}</td>
                      <td className="px-6 py-4">{appt.owner ?? '尚未指定飼主'}</td>
                      <td className="px-6 py-4 text-slate-500">{time}</td>
                      <td className="px-6 py-4">{appt.vetName}</td>
                      <td className="px-6 py-4">
                        <span className="pill bg-emerald-50 text-emerald-600">
                          {appt.status ?? '已排程'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Sidebar - Queue Status and Revenue */}
        <div className="space-y-4">
          {/* Queue Status */}
          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">佇列狀態</p>
              <h3 className="text-lg font-semibold text-slate-900">預約負載</h3>
            </div>
            <ul className="space-y-2 text-sm">
              {opsSummary.appointmentStatus.map((status) => {
                const percentage = Math.round((status.count / statusTotal) * 100);
                return (
                  <li key={status.status}>
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                      <span>{status.status}</span>
                      <span>{status.count}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-indigo"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Revenue with Trend - Enhanced & Interactive */}
          <div className="glass-panel space-y-4 p-6 ring-2 ring-brand-blue/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="section-title mb-1">財務脈動</p>
                <h3 className="text-lg font-semibold text-slate-900">收入概況</h3>
              </div>
              <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-600">
                已收 {revenueProgress}%
              </span>
            </div>
            {/* Legend / Filters */}
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
              {revenueDayLabels.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleFilterDay(i)}
                  className={`rounded-full px-2 py-1 transition border min-w-[34px] text-center ${
                    filteredRevenueIndex === i
                      ? 'bg-brand-blue text-white border-brand-blue'
                      : 'bg-slate-100 border-slate-200 hover:bg-brand-blue/10'
                  }`}
                  aria-pressed={filteredRevenueIndex === i}
                  aria-label={`篩選週期 第${d}天`}
                >
                  週{d}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setFilteredRevenueIndex(null)}
                className="rounded-full px-3 py-1 border border-slate-300 text-slate-600 hover:bg-slate-100"
              >全部</button>
            </div>

            {/* Revenue Trend Sparkline (interactive) */}
            <div
              className="relative h-16 flex items-end gap-1 outline-none"
              tabIndex={0}
              role="listbox"
              aria-label="最近 7 天收入趨勢 (方向鍵導航, Enter 深入, 空白篩選)"
              onKeyDown={handleSparklineKey}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) clearBarFocus();
              }}
            >
              {effectiveTrend.map((value, idx) => {
                const isActive = activeRevenueIndex === idx;
                const isFilteredZero = filteredRevenueIndex != null && filteredRevenueIndex !== idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => openDrillDown(idx)}
                    onFocus={() => handleBarFocus(idx)}
                    onMouseEnter={() => handleBarFocus(idx)}
                    onMouseLeave={clearBarFocus}
                    role="option"
                    aria-selected={isActive}
                    aria-label={`第 ${idx + 1} 天 收入 $${value}`}
                    className={`flex-1 rounded-t relative group transition-all focus:outline-none focus:ring-2 focus:ring-brand-blue/60 ${
                      isActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                    } ${isFilteredZero ? 'grayscale-[60%] opacity-40' : ''}`}
                    style={{ height: `${(value / maxRevenue) * 100}%`,
                      background: 'linear-gradient(to top,var(--color-accent),var(--color-accent-alt))'
                    }}
                  >
                    {isActive && (
                      <div className="absolute -top-8 left-1/2 z-10 w-max -translate-x-1/2 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        週{revenueDayLabels[idx]} • ${value}
                        {filteredRevenueIndex === idx && (
                          <span className="ml-1 rounded bg-brand-blue px-1 py-[1px] text-[9px] text-white">篩選中</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {activeRevenueIndex != null && (
              <p className="text-[11px] text-slate-500">
                使用 ← → 切換；Enter 深入分析；空白鍵{filteredRevenueIndex === activeRevenueIndex ? '清除篩選' : '篩選此日'}；Esc 取消高亮。
              </p>
            )}
            
            <p className="text-sm text-slate-500">
              待收 <span className="font-semibold text-slate-900">${Math.round(opsSummary.revenue.pending).toLocaleString()}</span>
              ，本週已收 <span className="font-semibold text-emerald-600">${Math.round(opsSummary.revenue.collected).toLocaleString()}</span>
              {filteredRevenueIndex != null && activeRevenueIndex === filteredRevenueIndex && (
                <span className="ml-2 rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-semibold text-brand-blue">僅顯示週{revenueDayLabels[filteredRevenueIndex]}</span>
              )}
            </p>
            
            <div className="h-3 rounded-full bg-slate-100">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-brand-blue to-brand-sky"
                style={{ width: `${Math.min(revenueProgress, 100)}%` }}
              />
            </div>
            
            {/* Action Button */}
            <button 
              onClick={() => navigate('/billing')}
              className="w-full rounded-xl border border-brand-blue bg-brand-blue/5 px-4 py-2 text-sm font-semibold text-brand-blue transition hover:bg-brand-blue hover:text-white"
            >
              <CreditCardIcon className="mr-2 inline-block h-4 w-4" />
              查看發票
            </button>

            {/* Drill-down modal */}
            {showRevenueModal && activeRevenueIndex != null && (
              <div
                role="dialog"
                aria-modal="true"
                aria-label={`週${revenueDayLabels[activeRevenueIndex]} 收入詳情`}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeDrillDown} />
                <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-800">
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">週{revenueDayLabels[activeRevenueIndex]} 收入拆解</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">總額 ${revenueBreakdown[activeRevenueIndex].total}</p>
                  <ul className="space-y-2 text-sm">
                    {revenueBreakdown[activeRevenueIndex].items.map(item => (
                      <li key={item.label} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-700/40">
                        <span className="font-medium text-slate-700 dark:text-slate-200">{item.label}</span>
                        <span className="text-slate-900 dark:text-slate-100 font-semibold">${item.amount}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { toggleFilterDay(activeRevenueIndex); closeDrillDown(); }}
                      className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:border-brand-blue hover:text-brand-blue"
                    >
                      {filteredRevenueIndex === activeRevenueIndex ? '取消篩選' : '篩選此日'}
                    </button>
                    <button
                      type="button"
                      onClick={closeDrillDown}
                      className="rounded-xl bg-brand-blue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-blue/90"
                    >
                      關閉
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        {/* Role-based Actions */}
        <div className="glass-panel space-y-4 p-6">
          <div>
            <p className="section-title mb-1">{playbook.title}</p>
            <h3 className="text-lg font-semibold text-slate-900">{playbook.headline}</h3>
          </div>
          <ul className="space-y-3 text-sm">
            {playbook.actions.map((action) => (
              <li
                key={action.label}
                className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 transition hover:border-brand-blue/30 hover:bg-brand-blue/5"
              >
                <p className="font-semibold text-slate-900">{action.label}</p>
                <p className="text-xs text-slate-500">{action.detail}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Pending Tasks / My Tasks */}
        <div className="glass-panel space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">我的任務</p>
              <h3 className="text-lg font-semibold text-slate-900">待辦事項</h3>
            </div>
            <span className="rounded-full bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-600">
              {pendingTasks.filter(t => t.priority === 'high').length} 緊急
            </span>
          </div>
          <ul className="space-y-2">
            {pendingTasks.map((task) => (
              <li
                key={task.id}
                className={`group flex items-start gap-3 rounded-xl border p-3 text-sm transition hover:border-brand-blue/50 hover:bg-brand-blue/5 ${
                  task.priority === 'high' 
                    ? 'border-rose-200 bg-rose-50/50' 
                    : 'border-slate-100 bg-white'
                }`}
              >
                <button className="mt-0.5 flex-shrink-0 rounded border-2 border-slate-300 p-0.5 transition group-hover:border-brand-blue">
                  <CheckCircleIcon className="h-4 w-4 text-transparent group-hover:text-brand-blue" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900">{task.title}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                    <ClockIcon className="h-3 w-3" />
                    <span>{task.dueDate}</span>
                    {task.priority === 'high' && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-600">
                        高優先
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <button className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-brand-blue hover:text-brand-blue">
            查看所有任務
          </button>
        </div>

        {/* Critical Alerts - Carousel */}
        <div className="glass-panel relative overflow-hidden p-6">
          <div className="mb-4">
            <p className="section-title mb-1">關鍵警示</p>
            <h3 className="text-lg font-semibold text-slate-900">臨床觀察列表</h3>
          </div>
          
          {/* Carousel Content */}
          <div className="relative min-h-[120px]">
            <div className="rounded-2xl border border-brand-blue/20 bg-gradient-to-br from-brand-blue/5 to-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-3xl">{currentAlert.icon}</span>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                  currentAlert.priority === 'high' 
                    ? 'bg-rose-100 text-rose-600'
                    : currentAlert.priority === 'medium'
                    ? 'bg-amber-100 text-amber-600'
                    : 'bg-emerald-100 text-emerald-600'
                }`}>
                  {currentAlert.priority === 'high' ? '高' : currentAlert.priority === 'medium' ? '中' : '低'}
                </span>
              </div>
              <p className="font-semibold text-slate-900">{currentAlert.title}</p>
              <p className="mt-1 text-sm text-slate-600">{currentAlert.detail}</p>
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={prevAlert}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-brand-blue hover:text-brand-blue"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <div className="flex gap-1">
              {criticalAlerts.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentAlertIndex(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === currentAlertIndex 
                      ? 'w-8 bg-brand-blue' 
                      : 'w-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={nextAlert}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-brand-blue hover:text-brand-blue"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;


